import React from 'react';
import { ReactComponent as SearchIcon } from '../../../assets/icons/StockChart/search-line.svg';
import { ReactComponent as TechnicalAnalysisIcon } from '../../../assets/icons/StockChart/analyst-icon.svg';
import { ReactComponent as ChartTypeIcon } from '../../../assets/icons/StockChart/calistic-icon.svg';
import { ReactComponent as TimePeriodIcon } from '../../../assets/icons/StockChart/clock-icon.svg';
import { ReactComponent as ComparisonIcon } from '../../../assets/icons/StockChart/compair-icon.svg';
import { ReactComponent as DrawingToolsIcon } from '../../../assets/icons/StockChart/edit-icon.svg';
import { ReactComponent as ViewIcon } from '../../../assets/icons/StockChart/eye-line.svg';
import { ReactComponent as SettingsIcon } from '../../../assets/icons/StockChart/settings-icon.svg';
import '../../../Components/Common/typography.css';
import './Toolbar.css';

export type PanelId =
  | 'timePeriod'
  | 'chartType'
  | 'drawingTools'
  | 'technicalAnalysis'
  | 'comparison'
  | 'view'
  | 'settings';

interface ToolbarButtonProps {
  id: PanelId;
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  isMobile: boolean;
  hasBadge?: number;
  onClick: (id: PanelId) => void;
  panelContent?: React.ReactNode;
}

const RIGHT_ALIGNED_PANELS: PanelId[] = [
  'technicalAnalysis',
  'comparison',
  'view',
  'settings',
];

const ToolbarButton: React.FC<ToolbarButtonProps> = ({
  id,
  label,
  icon,
  isActive,
  isMobile,
  hasBadge,
  onClick,
  panelContent,
}) => (
  <div className="toolbar-button-wrapper">
    <button
      className={`toolbar-button${isActive ? ' toolbar-button-active' : ''}`}
      onClick={() => onClick(id)}
      title={label}
    >
      <span className="toolbar-button-icon">{icon}</span>
      {isActive && !isMobile && <span className="header-4">{label}</span>}
      {hasBadge != null && hasBadge > 0 && (
        <span className="toolbar-badge">{hasBadge}</span>
      )}
    </button>
    {isActive && panelContent && (
      <div
        className={`toolbar-panel-anchor${RIGHT_ALIGNED_PANELS.includes(id) ? ' toolbar-panel-anchor-right' : ''}`}
      >
        {panelContent}
      </div>
    )}
  </div>
);

interface ToolbarProps {
  activePanel: PanelId | null;
  taCount: number;
  comparisonCount: number;
  onPanelToggle: (id: PanelId) => void;
  isMobile: boolean;
  isSearchOpen?: boolean;
  onSearchToggle?: () => void;
  panelContents?: Partial<Record<PanelId, React.ReactNode>>;
}

const BUTTONS: {
  id: PanelId;
  label: string;
  icon: React.ReactNode;
  badgeKey?: 'ta' | 'cmp';
}[] = [
  { id: 'timePeriod', label: 'Time Period', icon: <TimePeriodIcon /> },
  { id: 'chartType', label: 'Chart Type', icon: <ChartTypeIcon /> },
  { id: 'drawingTools', label: 'Drawing Tools', icon: <DrawingToolsIcon /> },
  {
    id: 'technicalAnalysis',
    label: 'Indicators',
    icon: <TechnicalAnalysisIcon />,
    badgeKey: 'ta',
  },
  {
    id: 'comparison',
    label: 'Compare',
    icon: <ComparisonIcon />,
    badgeKey: 'cmp',
  },
  { id: 'view', label: 'View', icon: <ViewIcon /> },
  { id: 'settings', label: 'Settings', icon: <SettingsIcon /> },
];

const Toolbar: React.FC<ToolbarProps> = ({
  activePanel,
  taCount,
  comparisonCount,
  onPanelToggle,
  isMobile,
  isSearchOpen,
  onSearchToggle,
  panelContents,
}) => (
  <div className={`chart-toolbar${isMobile ? ' chart-toolbar-mobile' : ''}`}>
    {isMobile && (
      <button
        className={`toolbar-button${isSearchOpen ? ' toolbar-button-active' : ''}`}
        onClick={onSearchToggle}
        title="Search"
      >
        <span className="toolbar-button-icon">
          <SearchIcon />
        </span>
      </button>
    )}
    {BUTTONS.map(({ id, label, icon, badgeKey }) => (
      <ToolbarButton
        key={id}
        id={id}
        label={label}
        icon={icon}
        isActive={activePanel === id}
        isMobile={isMobile}
        hasBadge={
          badgeKey === 'ta'
            ? taCount
            : badgeKey === 'cmp'
              ? comparisonCount
              : undefined
        }
        onClick={onPanelToggle}
        panelContent={panelContents?.[id]}
      />
    ))}
  </div>
);

export default Toolbar;
